import axios from 'axios'
import type { Ref } from 'vue'
import useLocalStorage from '@/composables/useLocalStorage'
import type { ContentScope, ContentScopeCreatorOption } from '@/types/contentScope'
import { isContentScope } from '@/utils/contentScope'

const LOCAL_ONLY_ROLES = ['manager', 'franchisee', 'operations-manager']
const CREATOR_OPTIONS_PAGE_SIZE = 1000

type ContentScopeResource = 'campaigns' | 'contest-games' | 'marketing-flows'

export const patchContentScope = async (resource: ContentScopeResource, id: number | string, scope: ContentScope) => {
  const { data } = await axios.patch<{ data: { scope: ContentScope } }>(`/${resource}/${id}/scope`, { scope })
  return data.data.scope
}

export const useContentScopeEntry = (scope: Ref<ContentScope | null | undefined> = ref(null)) => {
  const { canChooseScope } = useContentScope()
  const route = useRoute()

  const isScopeModalOpen = ref(false)

  const confirmScope = (value: ContentScope) => {
    scope.value = value
    isScopeModalOpen.value = false
  }

  const askScope = () => {
    if (!canChooseScope.value) {
      scope.value = 'local'
      return
    }
    if (scope.value) return
    if (isContentScope(route.query.scope)) {
      scope.value = route.query.scope
      return
    }
    isScopeModalOpen.value = true
  }

  return { scope, isScopeModalOpen, confirmScope, askScope }
}

export const useContentScopeSave = () => {
  const { t } = useI18n()
  const notification = useNotification()

  return async (request: () => Promise<ContentScope>) => {
    try {
      const saved = await request()
      notification.create('success', {
        text: t('contentScope.notifications.success', { scope: t(`contentScope.scopes.${saved}`) }),
        timeout: 3000
      })
      return saved
    } catch (error) {
      notification.create('error', { text: t('contentScope.notifications.error'), timeout: 3000 })
      throw error
    }
  }
}

export default function useContentScope() {
  const { user, isSupportConnection } = useLocalStorage()

  const canChooseScope = computed(
    () => isSupportConnection.value || !LOCAL_ONLY_ROLES.includes(user.value?.roleTemplate?.slug ?? '')
  )

  const currentUserId = computed(() => user.value?.id)

  const fetchCreatorOptions = async (): Promise<ContentScopeCreatorOption[]> => {
    try {
      const { data } = await axios.get<{
        data: Array<{ id: number; name: string | null; roleTemplate?: { name?: string } | null }>
      }>('/users', { params: { pageSize: CREATOR_OPTIONS_PAGE_SIZE } })

      return data.data.map((member) => ({
        id: member.id,
        name: member.name ?? '',
        role: member.roleTemplate?.name?.trim() || null
      }))
    } catch {
      const id = currentUserId.value
      return id == null ? [] : [{ id, name: user.value?.name ?? '', role: null }]
    }
  }

  return {
    canChooseScope,
    currentUserId,
    isSupportConnection,
    fetchCreatorOptions
  }
}
